import config from '../data/config.json';
import dict from '../data/dictionary.json';
import { divManBloq, divRegs, divPolis, plotPopupTemplate, poliPopupTemplate } from '../data/htmlTemplates.js';

import Mustache from 'mustache';
import { booleanIntersects } from '@turf/turf';
import L from './leaflet.circle-sector.js';
import mix from 'mix-css-color';
import Chart from 'chart.js/auto';

import { Sesion, Datos, Layers, obtenerURL } from '../main.js';
import { getPlotData, downloadPolygonPlots, storePolygon, retrievePolygon, deleteCachedPolygon } from './dataManager.js';
import { Mimapa } from './map.js';
import { ajustarColorRegiones, ajustarPopupsRegiones, selRegion } from './regions.js';
import { sumMeasureTaxon, correspondenciaTaxon, getTaxonLabel } from './taxons.js';
import { getLiteral, getDate, uriToLiteral, firstUppercase, getPreferredLang } from './util.js';
import { initTimedEvent, setEventData, sendTimedEvent, sendEvent } from './events.js';


let parcelasSP = {}; // las parcelas seleccionadas
let popupParPendiente = null; // para el caso en que hago un focus de una parcela 
							// y no puedo mostrar un popup porque no tengo los datos
					

// SELECCIÓN DE PARCELAS
function inicializarSP() {
	Sesion.estado.SP.provs = {}; // las provincias seleccionadas
	Sesion.estado.SP.munis = {}; // los municipios seleccionados
	Sesion.estado.SP.polis = []; // las coordenadas de los polígonos
	Datos.statsProvs = {};
	Datos.statsMunis = {};
	Datos.statsPolis = [];
	// para cada edición de inventario
	for (let ifn of config.ifnVersions) {
		parcelasSP[ifn] = new Set();
		Sesion.estado.SP[ifn] = {};
		Sesion.estado.SP[ifn].tx = []; // los taxones filtrados
		Sesion.estado.SP[ifn].txfilt = {}; // los filtros de los taxones filtrados (nuevo)
		Sesion.estado.SP[ifn].manual = {}; // las parcelas seleccionadas manualmente
		Sesion.estado.SP[ifn].bloq = {}; // las parcelas bloqueadas manualmente
	}
}

async function cargarSP(newsp) {
	// inicializo SP
	inicializarSP();
	
	// empiezo con los filtros de taxón
	for (let ifn of config.ifnVersions) {
		Sesion.estado.SP[ifn].tx = newsp[ifn].tx; // no interpreto lo que hay
		Sesion.estado.SP[ifn].txfilt = newsp[ifn].txfilt;
	}
	// inicializo Sesion.taxones y Sesion.taxonesFiltros
	Sesion.taxones = Sesion.estado.SP[Sesion.estado.ifn].tx;
	Sesion.taxonesFiltros = Sesion.estado.SP[Sesion.estado.ifn].txfilt;
	// inicializo Sesion.taxonesColores
	for (let ind = 0; ind < Sesion.taxones.length; ind++) {
		// elijo un color aleatoriamente entre los posibles si no hubiera color
		if (!Sesion.taxonesColores[ind]) {
			const cols = config.coltxinds.filter(function(col) {
				return !Sesion.taxonesColores.includes(col);
			});
			const cind = Math.floor(Math.random() * cols.length);
			Sesion.taxonesColores.push(cols[cind]);
		}	
	}	
	
	// sigo con la selección manual y bloqueos de las parcelas
	for (let ifn of config.ifnVersions) {
		for (const piri in newsp[ifn].manual)
			manualParcelaSP(ifn, piri);
		for (const piri in newsp[ifn].bloq)
			bloqueoParcelaSP(ifn, piri);
		// recupero los datos de las parcelas manuales y bloqueadas
		const piris =[...Object.keys(newsp[ifn].manual), ...Object.keys(newsp[ifn].bloq)];
		if (piris.length > 0)
			await getPlotData(piris, ifn);
	}
	// actualizo vista manual-bloqueos
	// repinto parcelas
	ajustarColorParcelas(Object.keys(newsp[Sesion.estado.ifn].manual));
	ajustarColorParcelas(Object.keys(newsp[Sesion.estado.ifn].bloq));
	// llamo a recalcular las parcelas seleccionadas
	actualizarInfoSPURL();
	// actualización botones manual-bloq
	actualizarBotonesManBloq();	
	
	// sigo con las provincias
	for (const priri in newsp.provs)
		await selRegion(priri);
	
	// sigo con los municipios
	for (const miri in newsp.munis)
		await selRegion(miri);
		
	// pongo los municipios arriba (no sé por qué no lo hace bien si pinto más tarde los municipios)
	for (const miri in Sesion.estado.SP.munis) {
		const mlayer = Layers.regs.getLayers().find(layer => layer.feature.properties.iri === miri);
		mlayer.bringToFront();
	}
		
	// termino con los polígonos
	Layers.polis.clearLayers(); // borro la capa de polígonos
	Datos.polis = []; // borro los datos de parcelas de los polígonos
	for (const poli of newsp.polis) {
		if (Array.isArray(poli)) {
			// CASO POLÍGONO DIBUJADO, poli SON COORDENADAS		
			// primero dibujo el polígono
			const lp = L.polygon(poli, {
				fillColor: config.colores[config.colregselind][2], // color de relleno  
				weight: 2,
				opacity: 1,
				color: config.colores[config.colregselind][4],
				dashArray: '1',
				fillOpacity: 0.4
			});
			// lo añado a la capa de polígonos
			lp.addTo(Layers.polis);
			// incluyo poli a la selección
			await incluirPoligonoSP(lp);
		}
		else {
			// CASO POLÍGONO SUBIDO
			try {
				// recupero el polígono en geojson
				const migeojson = await retrievePolygon(poli);
				// lo dibujamos
				const migeolayer = L.geoJSON(migeojson, {
					style: function (feature) {
						return {
							fillColor: config.colores[config.colregselind][2], // color de relleno  
							weight: 2,
							opacity: 1,
							color: config.colores[config.colregselind][4],
							dashArray: '1',
							fillOpacity: 0.4
						};
					}
				});
				// lo añado a la capa de polígonos
				migeolayer.addTo(Layers.polis);
				// preparo objeto con todo lo necesario para la selección
				const mipoliaux = {
					id : poli,
					geojson: migeojson,
					cachear: false								
				};
				// incluyo poli a la selección
				await incluirPoligonoSP(migeolayer, mipoliaux);
			}
			catch(error) {
				console.error("No está " + poli + " en la caché. No podemos hacer nada más...", error);			
			}
		}
	}
}


// PROVINCIAS Y MUNICIPIOS
function cambiarSeleccionRegionSP(riri) {
	// extraigo datos
	const reg = Datos.provs[riri]? Datos.provs[riri] : Datos.munis[riri];
	const spregs = Datos.provs[riri]? Sesion.estado.SP.provs : Sesion.estado.SP.munis;
	const statsregs = Datos.provs[riri]? Datos.statsProvs : Datos.statsMunis;
	const regtype = Datos.provs[riri]? 'province' : 'municipality';
	const regscontra = Datos.provs[riri]? Datos.munis : Datos.provs;
	const spregscontra = Datos.provs[riri]? Sesion.estado.SP.munis : Sesion.estado.SP.provs;
		
	let borrada = false; // controlo si borro la capa al deseleccionar
	
	if (spregs[riri]) { // estaba seleccionada
		// quito la región
		delete spregs[riri];
		delete statsregs[riri];
		// obtengo intersecciones con otros polígonos para optimizar la actualización de las parcelas
		const layerreg = Object.values(Layers.regs._layers).find(layer => layer.feature.properties.iri === riri);	
		const interpolis = [];
		for (let pind = 0; pind < Sesion.estado.SP.polis.length; pind++) {
			const layerpoli = Layers.polis.getLayers()[pind];
			if (booleanIntersects(layerpoli.toGeoJSON(), layerreg.feature.geometry))
				interpolis.push(pind);		
		}
		// obtengo intersecciones con las regiones contrarias para optimizar la actualización de las parcelas
		const interregs = [];
		for (const riricontra in spregscontra) {
			const layerregcontra = Object.values(Layers.regs._layers).find(layer => layer.feature.properties.iri === riricontra);
			if (booleanIntersects(layerregcontra.feature.geometry, layerreg.feature.geometry))
				interregs.push(riricontra);		
		}
		// actualizo las parcelas seleccionadas por cada edición de inventario
		for (const ifn of config.ifnVersions) {
			for (const piri of reg.plots[ifn]) {
				if (!Sesion.estado.SP[ifn].manual[piri]) { // tengo en cuenta que no esté en manual
					// tengo en cuenta que no esté incluida en un polígono
					let incluida = false;
					for (const pind of interpolis) {
						if (Datos.polis[pind][ifn].includes(piri)) {
							incluida = true;
							break;
						}
					}
					// tengo en cuenta que no esté en una región contraria
					if (!incluida) {
						for (const riricontra of interregs) {
							if (regscontra[riricontra].plots[ifn].includes(piri)) {
								incluida = true;
								break;
							}
						}
					}
					if (!incluida)
						parcelasSP[ifn].delete(piri);
				}
			}
		}
		// elimino la capa si fuera necesario
		if ((Sesion.estado.mapDetails === "munis" && regtype === "province") || 
				(Sesion.estado.mapDetails === "provs" && regtype === "municipality")) {
			Layers.regs.removeLayer(layerreg);
			borrada = true;
		}
		else // pongo la capa abajo
			layerreg.bringToBack();
		// mando evento deselección de provincia GA
		sendEvent( 'deselect_content', { content_type: regtype, content_id: getLiteral(reg.label) } );
	}
	else { // no estaba seleccionada
		// incluyo la región
		spregs[riri] = true;
		// inicializo estadísticos
		statsregs[riri] = {};		
		// actualizo las parcelas seleccionadas por cada edición de inventario
		for (const ifn of config.ifnVersions) {
			// estadísticos de la edición
			statsregs[riri][ifn] = {
				total: reg.plots[ifn].length,
				manual: 0,
				bloq: 0,
				filt: 0
			};
			for (const piri of reg.plots[ifn]) {
				if (Sesion.estado.SP[ifn].bloq[piri])
					statsregs[riri][ifn].bloq++;
				else if (Sesion.estado.SP[ifn].manual[piri]) {
					parcelasSP[ifn].add(piri); // incluyo siempre en manual
					statsregs[riri][ifn].manual++;
				}
				else if (!parcelaFiltrada(ifn, piri)) // incluyo si no está filtrada
					parcelasSP[ifn].add(piri);
				else
					statsregs[riri][ifn].filt++; // sólo para estadísticos
			}
			// incluyo info num de parcelas GA	
			setEventData('num_plots_'+ifn, reg.plots[ifn].length);			
		}
		// mando evento temporizado selección de región GA (datos preparados de antes)
		sendTimedEvent();
	}
	
	// actualizo vista
	// si no se borró: repinto la región y ajusto popup 
	if (!borrada) {
		ajustarColorRegiones([riri]);
		ajustarPopupsRegiones([riri]);
	}
	// repinto las parcelas y sus popups de la edición del ifn seleccionada	
	ajustarColorParcelas(reg.plots[Sesion.estado.ifn]);
	ajustarPopupsParcelas(reg.plots[Sesion.estado.ifn]);
	// llamo a recalcular las parcelas seleccionadas
	actualizarInfoSPURL();
	// actualización botones región
	actualizarBotonesRegion();
}

function actualizarBotonesRegion() {
	if (Object.keys(Sesion.estado.SP.provs) == 0 && Object.keys(Sesion.estado.SP.munis) == 0) // escondo li regiones
		document.getElementById('li-regs').classList.add('d-none');
	else { 
		// preparo objeto plantilla
		const tobj = [];
		for (const priri in Sesion.estado.SP.provs) {
			tobj.push( { 
				iri: priri, name: getLiteral(Datos.provs[priri].label),
				cant: Datos.statsProvs[priri][Sesion.estado.ifn].total - Datos.statsProvs[priri][Sesion.estado.ifn].filt - Datos.statsProvs[priri][Sesion.estado.ifn].bloq
				//cant: Datos.provs[priri].plots[Sesion.estado.ifn].length 
			} );
		}
		for (const miri in Sesion.estado.SP.munis) {
			tobj.push( { 
				iri: miri, name: getLiteral(Datos.munis[miri].label),
				cant: Datos.statsMunis[miri][Sesion.estado.ifn].total - Datos.statsMunis[miri][Sesion.estado.ifn].filt - Datos.statsMunis[miri][Sesion.estado.ifn].bloq
			} );
		}
		// actualizo div
		const html = Mustache.render(divRegs, tobj);
		document.getElementById('li-regs').innerHTML = html;		
		// muestro
		document.getElementById('li-regs').classList.remove('d-none');
		// extraigo la lista de regiones
		const riris = [
			...Object.keys(Sesion.estado.SP.provs),
			...Object.keys(Sesion.estado.SP.munis)
		];		
		// listeners de los botones de regiones
		for (const riri of riris) {
			// botón borrar
			document.querySelector('.borrar[iri="'+riri+'"]').addEventListener('click', function() {
			    cambiarSeleccionRegionSP(riri);
			});
			// botón focus
			document.querySelector('.focus[iri="'+riri+'"]').addEventListener('click', function() {
				// obtengo el layer de la provincia
				const layer = Object.values(Layers.regs._layers).find(layer => layer.feature.properties.iri === riri);
				// navegamos a la región
				Mimapa.flyToBounds(layer.getBounds(), {animate: true, duration: 0.4});
				// abro el popup en 0.45s
				setTimeout(function() {
					Mimapa.closePopup();
					layer.openPopup();	
				}, 450);
			});
		}
	}
}


// POLÍGONOS
function popupPoligon(pind) {
	// devuelvo un objeto con el popup y las opciones de rendering
	const out = {
		popup: {},
		opcs: {} // caso sin canvas
	};
	// preparo plantilla para el mustache
	const politemp = {};
	// iri
	politemp.iri = "poli"+pind;
	politemp.poli = getLiteral(dict.polygon) + " #" + pind; // etiqueta con el nombre
	// fuente
	politemp.source = getLiteral(dict["source" + Sesion.estado.ifn]);
	// sampledplots
	if (Datos.polis[pind][Sesion.estado.ifn].length == 0)
		politemp.sampledplots = getLiteral(dict.nosampledplots);
	else {
		if (Datos.polis[pind][Sesion.estado.ifn].length == 1)
			politemp.sampledplots = getLiteral(dict.sampledplot);
		else
			politemp.sampledplots = Datos.polis[pind][Sesion.estado.ifn].length + " " + getLiteral(dict.sampledplots);
		politemp.chart = true;
		out.opcs = {minWidth: 320};	
	}
	// obtengo el popup y lo devuelvo
	out.popup = Mustache.render(poliPopupTemplate, politemp);
	
	return out;
}
function ponerChartPopupPoli(canvas, pind) {
	// muestro el canvas
	canvas.classList.remove("d-none");	
	// preparo los datos del chart
	const labels = [];
	const valores = [];
	const colores = [];	
	// manual
	if (Datos.statsPolis[pind][Sesion.estado.ifn].manual > 0) {
		labels.push(getLiteral(dict.manuallySelected));
		valores.push(Datos.statsPolis[pind][Sesion.estado.ifn].manual);
		colores.push(config.colores[config.colregselind][8]);
	}
	// seleccionadas 
	const nsel = Datos.statsPolis[pind][Sesion.estado.ifn].total 
		- Datos.statsPolis[pind][Sesion.estado.ifn].manual	
		- Datos.statsPolis[pind][Sesion.estado.ifn].filt
		- Datos.statsPolis[pind][Sesion.estado.ifn].bloq;
	if (nsel > 0) {
		labels.push(getLiteral(dict.selected));
		valores.push(nsel);
		colores.push(config.colores[config.colregselind][3]);
	}
	// filtered
	if (Datos.statsPolis[pind][Sesion.estado.ifn].filt > 0) {
		labels.push(getLiteral(dict.filtered));
		valores.push(Datos.statsPolis[pind][Sesion.estado.ifn].filt);
		colores.push(config.colores[config.colplotind][4]);
	}
	// blocked
	if (Datos.statsPolis[pind][Sesion.estado.ifn].bloq > 0) {
		labels.push(getLiteral(dict.blocked));
		valores.push(Datos.statsPolis[pind][Sesion.estado.ifn].bloq);
		colores.push(config.colblack);
	}
	// preparo el chart
	const chartconfig =  {
		type: 'doughnut',
		data: {
			labels: labels,
			datasets: [{
				label: getLiteral(dict.nplots),
				data: valores,
				backgroundColor: colores,
				hoverOffset: 4
			}]
		},
		options: {
			animation: false
		}
	};	
	// pinto
	new Chart(canvas, chartconfig);	
}


async function incluirPoligonoSP(layer, mipoliaux) {
	// inicializo TimedEvent GA
	initTimedEvent({
		content_type: 'polygon'
	});
		
	// si es un polígono creado por fichero (mipoliaux) hay que hacer más cosas
	let polinfo = layer._latlngs; // coordenadas si no fue por fichero
	if (mipoliaux) {
		// guardo en polinfo el id
		polinfo = mipoliaux.id;
		// pido guardar el polígono si hay que cachear
		if (mipoliaux.cachear) {
			await storePolygon(mipoliaux);
			// mando evento de polígono creado
			sendEvent( 'create_content', { content_type: 'polygon', content_id: mipoliaux.id, format: mipoliaux.format } );				
		}
	}
		
	// puede ser un polígono o un geojson (con carga de ficheros)
	const feat = {
		type: "polygon",
		bounds: layer._bounds? layer._bounds : layer.getBounds(),
		feature: mipoliaux? mipoliaux.geojson : layer.toGeoJSON(), 
		labprog: '<i>' + getLiteral(dict.downloadingPolygon) + '</i>'
	};
	
	/*
	// extraigo coordenadas, bounds y feature (geojson)
	const coords = layer._latlngs;
	const feat = {
		type: "polygon",
		bounds: layer._bounds,
		feature: layer.toGeoJSON(), 
		labprog: '<i>' + getLiteral(dict.downloadingPolygon) + '</i>'
	};
	const bounds = layer._bounds;
	const feat = layer.toGeoJSON();*/
	
	// inicializo temporizador
	const npol = Sesion.estado.SP.polis.length;
	console.time("Descarga parcelas polígono #" + npol);
		
	// pido datos (si hace falta)
	const promesadatos = mipoliaux && Datos.poliplots[mipoliaux.id]? null : downloadPolygonPlots(feat);
	//const promesadatos = downloadPolygonPlots(bounds, feat,  '<i>' + getLiteral(dict.downloadingPolygon) + '</i>');
	
	// mientras tanto pongo por encima las parcelas pintadas
	Layers.parcs.eachLayer(function (parc) {
	    parc.bringToFront();
	});
	
	// meto polinfo en el estado
	Sesion.estado.SP.polis.push(polinfo);	
	// guardo coords del polígono en el estado
	//Sesion.estado.SP.polis.push(coords);
	
	// ahora espero a la promesa (si no lo tenía)
	const poliplots = promesadatos? await promesadatos : Datos.poliplots[mipoliaux.id];	
	//const poliplots = await promesadatos;
	
	// fin temporizador
	console.timeEnd("Descarga parcelas polígono #" + npol);
	
	// guardo los datos
	Datos.polis.push(poliplots);
	if (mipoliaux && !Datos.poliplots[mipoliaux.id]) // cacheo especial para evitar recálculos
		Datos.poliplots[mipoliaux.id] = poliplots;
	
	// inicializo estadísticos y guardo	
	const stats = {};
	Datos.statsPolis.push(stats);

	// actualizo las parcelas seleccionadas
	for (let ifn of config.ifnVersions) {
		stats[ifn] = {
			total: poliplots[ifn].length,
			manual: 0,
			bloq: 0,
			filt: 0
		};
		for (const piri of poliplots[ifn]) {
			if (Sesion.estado.SP[ifn].bloq[piri])
				stats[ifn].bloq++;
			else if (Sesion.estado.SP[ifn].manual[piri]) {
				parcelasSP[ifn].add(piri); // incluyo siempre en manual
				stats[ifn].manual++;
			}
			else if (!parcelaFiltrada(ifn, piri)) // incluyo si no está filtrada
				parcelasSP[ifn].add(piri);
			else
				stats[ifn].filt++; // sólo para estadísticos
			
			// incluyo info num de parcelas GA	
			setEventData('num_plots_'+ifn, poliplots[ifn].length);
		}
	}
	// mando evento temporizado selección de polígono GA
	sendTimedEvent();
	
	// actualizo vista	
	// repinto las parcelas y sus popups de la edición del ifn seleccionada
	ajustarColorParcelas(poliplots[Sesion.estado.ifn]);
	ajustarPopupsParcelas(poliplots[Sesion.estado.ifn]);	
	
	// llamo a recalcular las parcelas seleccionadas
	actualizarInfoSPURL();
	
	// actualización botones polígonos (incluye popups)
	actualizarBotonesPolis();
}


function borrarPoligonoSP(idpoli) {	
	// obtengo intersecciones con provincias y otros polígonos para optimizar la actualización de las parcelas
	const layerborrar = Layers.polis.getLayers()[idpoli];
	const interprovs = [];
	for (const priri of Object.keys(Sesion.estado.SP.provs)) {
		const layerpr = Object.values(Layers.regs._layers).find(layer => layer.feature.properties.iri === priri);
		if (booleanIntersects(layerpr.feature.geometry, layerborrar.toGeoJSON()))
			interprovs.push(priri);
	}
	const interpolis = [];
	for (let pind = 0; pind < Sesion.estado.SP.polis.length; pind++) {
		if (pind !== idpoli) {
			const layerpoli = Layers.polis.getLayers()[pind];
			if (booleanIntersects(layerpoli.toGeoJSON(), layerborrar.toGeoJSON()))
				interpolis.push(pind);		
		}
	}
	
	// recupero los datos de parcelas a borrar
	const poliplots = Datos.polis[idpoli];	
	
	// actualizo las parcelas seleccionadas
	for (const ifn of config.ifnVersions) {
		for (const piri of poliplots[ifn]) {
			if (!Sesion.estado.SP[ifn].manual[piri]) { // tengo en cuenta que no esté en manual
				// tengo en cuenta que no esté incluida en una provincia 
				let incluida = false;
				for (const priri of interprovs) {
					if (Datos.provs[priri].plots[ifn].includes(piri)) {
						incluida = true;
						break;
					}						
				}
				// tengo en cuenta que no esté incluida en otro polígono
				if (!incluida) {
					for (const pind of interpolis) {
						if (Datos.polis[pind][ifn].includes(piri)) {
							incluida = true;
							break;
						}						
					}
				}
				if (!incluida)
					parcelasSP[ifn].delete(piri);
			}
		}
	}
		
	// borrado especial del objeto en la caché
	if (!Array.isArray(Sesion.estado.SP.polis[idpoli])) {
		// borro poliplots
		delete Datos.poliplots[Sesion.estado.SP.polis[idpoli]];
		// borro caché
		deleteCachedPolygon(Sesion.estado.SP.polis[idpoli]);
		/*
		const polurl = config.craftsConfig.api + "/object?id=" + Sesion.estado.SP.polis[idpoli];
		Crafts.borradoCache(polurl); // no me hace falta esperar
		*/
	}
	
	// borro los datos de parcelas, los estadísticos, el polígono del estado y la capa
	Datos.polis.splice(idpoli, 1);
	Datos.statsPolis.splice(idpoli, 1);
	Sesion.estado.SP.polis.splice(idpoli, 1);
	Layers.polis.removeLayer(Layers.polis.getLayers()[idpoli]);
	
	// mando evento borrado de polígono GA
	sendEvent( 'delete_content', { content_type: 'polygon' } );
	
	// actualizo vista
	// repinto las parcelas y sus popups de la edición del ifn seleccionada
	ajustarColorParcelas(poliplots[Sesion.estado.ifn]);
	ajustarPopupsParcelas(poliplots[Sesion.estado.ifn]);
	// llamo a recalcular las parcelas seleccionadas
	actualizarInfoSPURL();
	// actualización botones provincia
	actualizarBotonesPolis();
}

function actualizarBotonesPolis() {
	if (Sesion.estado.SP.polis.length == 0) // escondo li polis
		document.getElementById('li-polis').classList.add('d-none');
	else { // preparo objeto plantilla
		const tobj = [];
		for (let ind = 0; ind < Sesion.estado.SP.polis.length; ind++)
			tobj.push( { ind: ind, 
				cant: Datos.statsPolis[ind][Sesion.estado.ifn].total - Datos.statsPolis[ind][Sesion.estado.ifn].filt - Datos.statsPolis[ind][Sesion.estado.ifn].bloq
			} );
			//cant: Datos.polis[ind][Sesion.estado.ifn].length } );			
		// actualizo div
		const html = Mustache.render(divPolis, tobj);
		document.getElementById('li-polis').innerHTML = html;		
		// muestro
		document.getElementById('li-polis').classList.remove('d-none');
				
		// listeners de los botones de polígonos y doble click de borrar en el polígono
		for (let ind = 0; ind < Sesion.estado.SP.polis.length; ind++) {
			// botón borrar
			document.getElementById("poli"+ind+"-borrar").addEventListener('click', function() {
			    borrarPoligonoSP(ind);
			});
			// botón focus => navegamos al polígono
			document.getElementById("poli"+ind+"-focus").addEventListener('click', function() {
				Mimapa.flyToBounds(Layers.polis.getLayers()[ind].getBounds(), {animate: true, duration: 0.4});
				// abro el popup en 0.45s
				setTimeout(function() {
					Mimapa.closePopup();
					Layers.polis.getLayers()[ind].openPopup();
				}, 450);
			});
			 // quito listeners previos de popup y doble click (por el posible reordenamiento de los polígonos)
			Layers.polis.getLayers()[ind].unbindPopup();
			Layers.polis.getLayers()[ind].off('dblclick');
			// popup polígono
			const op = popupPoligon(ind); // obtengo el popup
			Layers.polis.getLayers()[ind].bindPopup(op.popup, op.opcs);
			// doble click en el polígono del mapa
			Layers.polis.getLayers()[ind].on('dblclick ', function(e) {
				borrarPoligonoSP(ind);
			});
		}
	}
}


// SELECCIÓN PARCELAS
function manualParcelaSP(ifn, piri) {
	parcelasSP[ifn].add(piri);
	Sesion.estado.SP[ifn].manual[piri] = true;
	delete Sesion.estado.SP[ifn].bloq[piri];
	// mando evento selección de parcela GA
	sendEvent( 'select_content', { content_type: 'plot', content_id: piri } );
}

function bloqueoParcelaSP(ifn, piri) {
	parcelasSP[ifn].delete(piri);
	delete Sesion.estado.SP[ifn].manual[piri];
	Sesion.estado.SP[ifn].bloq[piri] = true;
	// mando evento bloqueo de parcela GA
	sendEvent( 'block_content', { content_type: 'plot', content_id: piri } );
}

async function cambiarSeleccionParcelaSP(piri, cambioboton) {
	/*
	// cierro el popup
	if (Sesion.parPintadas[piri]) {
		if (Array.isArray(Sesion.parPintadas[piri])) {
			for (let sc of Sesion.parPintadas[piri])
				sc.closePopup();
		}
		else 
			Sesion.parPintadas[piri].closePopup();
	}*/
	
	// primero detecto si está incluida en alguna región
	let riri = null;
	let streg = null;
	// detecto inclusión en las provincias
	for (const priri in Sesion.estado.SP.provs) {
		if (Datos.provs[priri].plots[Sesion.estado.ifn].includes(piri)) {
			riri = priri;
			streg = Datos.statsProvs[riri];
			break; // máximo 1
		}
	}
	// detecto inclusión en los municipios
	for (const miri in Sesion.estado.SP.munis) {
		if (Datos.munis[miri].plots[Sesion.estado.ifn].includes(piri)) {
			riri = miri;
			streg = Datos.statsMunis[riri];
			break; // máximo 1
		}
	}
	
	// detecto inclusión en los polígonos (para todos)	
	const polinds = [];
	for (let pind=0; pind<Datos.polis.length; pind++) {
		if (Datos.polis[pind][Sesion.estado.ifn].includes(piri))
			polinds.push(pind);
	}
	// booleano de si la parcela está incluida en alguna parte
	const incluida = riri != null || polinds.length > 0; 
	
	// hay que ver si se trata de (1) selección manual (2) bloqueo o (3) defecto
	let manual = false;
	let bloqueo = false;
	let defecto = cambioboton; // por defecto si hubo cambioboton
	
	// analizamos los casos de doble click en una parcela del mapa
	if (!cambioboton) {
		if (parcelasSP[Sesion.estado.ifn].has(piri)) // estaba seleccionada
			bloqueo = true;	// paso a bloquearla
		else { // NO estaba seleccionada
			if (Sesion.estado.SP[Sesion.estado.ifn].bloq[piri])
				defecto = true; // quito bloqueo
			else
				manual = true; // pongo manual
		}
		/*
		if (parcelasSP[Sesion.estado.ifn].has(piri)) { // estaba seleccionada
			if (Sesion.estado.SP[Sesion.estado.ifn].manual[piri])
				defecto = true;
			else
				bloqueo = true;	
		}
		else { // NO estaba seleccionada
			if (Sesion.estado.SP[Sesion.estado.ifn].bloq[piri])
				defecto = true;
			else
				manual = true;		
		}*/
	}
	
	// actuamos según el caso para todos los inventarios
	for (const ifnev of config.ifnVersions) {
		const piriev = ifnev === Sesion.estado.ifn? piri : Datos.parcelas[piri]["plot"+ifnev];
		if (piriev) { // sólo si hay parcela
			// recupero los datos de la parcela para evitar este problema:
			// selecciono una parcela en el IFN3, voy a descargar y selecciono el inventario IFN2
			// => fallará si no se descargó antes los datos de la parcela para el IFN2
			await getPlotData([piriev], ifnev);
			if (manual) {
				manualParcelaSP(ifnev, piriev);
				// estats
				if (riri) {
					streg[ifnev].manual++;
					streg[ifnev].filt--;
				}
				for (const pind of polinds) {
					Datos.statsPolis[pind][ifnev].manual++;
					Datos.statsPolis[pind][ifnev].filt--;
				}
			}
			else if (bloqueo) {
				bloqueoParcelaSP(ifnev, piriev);
				// estats
				if (riri)
					streg[ifnev].bloq++;
				for (const pind of polinds)
					Datos.statsPolis[pind][ifnev].bloq++;
			}
			else if (defecto) {
				// mando evento deselección de parcela GA
				sendEvent( 'deselect_content', { content_type: 'plot', content_id: piri } );
				// elimino selección, manual y bloqueo
				parcelasSP[ifnev].delete(piriev);
				if (Sesion.estado.SP[ifnev].manual[piriev]) {
					delete Sesion.estado.SP[ifnev].manual[piriev];
					// estats
					if (riri)
						streg[ifnev].manual--;
					for (const pind of polinds)
						Datos.statsPolis[pind][ifnev].manual--;					
				}
				if (Sesion.estado.SP[ifnev].bloq[piriev]) {
					delete Sesion.estado.SP[ifnev].bloq[piriev];
					// estats
					if (riri)
						streg[ifnev].bloq--;
					for (const pind of polinds)
						Datos.statsPolis[pind][ifnev].bloq--;					
				}
				// trato el caso de incluida
				if (incluida) {
					if (!parcelaFiltrada(ifnev, piriev)) // incluyo si no está filtrada
						parcelasSP[ifnev].add(piriev);
					else { // sólo estats
						if (riri)
							streg[ifnev].filt++;
						for (const pind of polinds)
							Datos.statsPolis[pind][ifnev].filt++;
					}
				}	
			}
		}
	}
	
	// actualizo vista
	// repinto la parcela
	// caso muy especial: el ajuste de color puede provocar borrar layers de la parcela (si hay varios colores)
	// con lo que el popup desaparecería. Para evitar eso, compruebo antes el popup
	let latlng = null
	if (Sesion.parPintadas[piri] && Sesion.popup && Sesion.popup.isOpen()) {
		if (Sesion.popup.getElement().querySelector(".popup")	
				&& Sesion.popup.getElement().querySelector(".popup").getAttribute("iri") === piri) 
			latlng = Sesion.popup.getLatLng();
	}
	ajustarColorParcelas([piri]);	
	// reajusto el popup y abro si no estoy en cambioboton
	ajustarPopupsParcelas([piri]);
	// si se perdió el popup, lo muestro
	if (Sesion.popup && !Sesion.popup.isOpen() && latlng) {
		if (Array.isArray(Sesion.parPintadas[piri]))
			Sesion.parPintadas[piri][0].openPopup(latlng);
		else 
			Sesion.parPintadas[piri].openPopup(latlng);	
	}
	// popup región
	if (riri)
		ajustarPopupsRegiones([riri]);
	// llamo a recalcular las parcelas seleccionadas
	actualizarInfoSPURL();
	// actualización botones manual-bloq
	actualizarBotonesManBloq();
	// actualización botones provs (por los estadísticos)
	actualizarBotonesRegion();
	// actualización botones polis (por los estadísticos) y popups
	actualizarBotonesPolis();
}



function actualizarBotonesManBloq() {
	const nmanual = Object.keys(Sesion.estado.SP[Sesion.estado.ifn].manual).length;
	const nbloq = Object.keys(Sesion.estado.SP[Sesion.estado.ifn].bloq).length;
	if (nmanual == 0 && nbloq == 0) {
		// escondo
		document.getElementById('li-man-bloq').classList.add('d-none');
	}
	else { // preparo objeto plantilla
		const tobj = {};
		if (nmanual > 0) {
			tobj.man = true;
			tobj.nman = nmanual;
		}
		if (nbloq > 0) {
			tobj.bloq = true;
			tobj.nbloq = nbloq;
		}
		// actualizo div
		const html = Mustache.render(divManBloq, tobj);
		document.getElementById('li-man-bloq').innerHTML = html;		
		// muestro
		document.getElementById('li-man-bloq').classList.remove('d-none');
		
		// listener manual
		if (nmanual > 0) {
			// borrar
			document.getElementById("bot-manual-borrar").addEventListener('click', function() {
			    for (const piri in Sesion.estado.SP[Sesion.estado.ifn].manual) 
		    		cambiarSeleccionParcelaSP(piri, true);
			});
			// focus
			document.getElementById("bot-manual-focus").addEventListener('click', function() {
				// actualizo índice manual
				Sesion.indMan++;
				if (Sesion.indMan >= nmanual) Sesion.indMan = 0;
				// recupero la localización de la parcela
				const piri = Object.keys(Sesion.estado.SP[Sesion.estado.ifn].manual)[Sesion.indMan];
				const coords = [Datos.parcelas[piri].lat, Datos.parcelas[piri].lng];
				// navegamos a la parcela
				Mimapa.flyTo(coords, config.zLugar, {animate: true, duration: 0.4});
				// abro el popup en 0.6s
				setTimeout(function() {
					if (Sesion.parPintadas[piri]) {
						// si sólo hay uno, pasa algo raro con la rotación, así que detecto si es el mismo
						if (Sesion.popup && Sesion.popup.isOpen()) {
							const popel = Sesion.popup.getElement();
							const popupel = popel.querySelector(".popup");
							if (popupel && popupel.classList.contains("parcela")) {
								const iripopup = popel.querySelector(".popup").getAttribute("iri");
								if (piri === iripopup)
									return; //no tiene sentido abrir un popup ya abierto (y que fastidia el canvas por ello)
							}
						}
						// abro el popup en otro caso
						if (Array.isArray(Sesion.parPintadas[piri]))
							Sesion.parPintadas[piri][0].openPopup();
						else 
							Sesion.parPintadas[piri].openPopup();
					}
					else
						popupParPendiente = piri; // habrá que abrirlo en cuanto se pinte
				}, 500);
			});
		}
		
		// listener blocked
		if (nbloq > 0) {
			// borrar
			document.getElementById("bot-blocked-borrar").addEventListener('click', function() {
			    for (const piri in Sesion.estado.SP[Sesion.estado.ifn].bloq) 
			    	cambiarSeleccionParcelaSP(piri, true);
			});
			// focus
			document.getElementById("bot-blocked-focus").addEventListener('click', function() {
				// actualizo índice blocked
				Sesion.indBloq++;
				if (Sesion.indBloq >= nbloq) Sesion.indBloq = 0;
				// recupero la localización de la parcela
				const piri = Object.keys(Sesion.estado.SP[Sesion.estado.ifn].bloq)[Sesion.indBloq];
				const coords = [Datos.parcelas[piri].lat, Datos.parcelas[piri].lng];
				// navegamos a la parcela
				Mimapa.flyTo(coords, config.zLugar, {animate: true, duration: 0.4});
				// abro el popup en 0.6s
				setTimeout(function() {
					if (Sesion.parPintadas[piri]) {
						// si sólo hay uno, pasa algo raro con la rotación, así que detecto si es el mismo
						if (Sesion.popup && Sesion.popup.isOpen()) {
							const popel = Sesion.popup.getElement();
							const popupel = popel.querySelector(".popup");
							if (popupel && popupel.classList.contains("parcela")) {
								const iripopup = popel.querySelector(".popup").getAttribute("iri");
								if (piri === iripopup)
									return; //no tiene sentido abrir un popup ya abierto (y que fastidia el canvas por ello)
							}
						}
						// abro el popup en otro caso
						if (Array.isArray(Sesion.parPintadas[piri]))
							Sesion.parPintadas[piri][0].openPopup();
						else 
							Sesion.parPintadas[piri].openPopup();
					}
					else
						popupParPendiente = piri; // habrá que abrirlo en cuanto se pinte
				}, 500);
			});
		}
	}
}

// INFO NUM PARCELAS Y ACTUALIZACIÓN URL
function actualizarInfoSPURL() {
	// actualizo la info del número de parcelas
	document.getElementById('numplots').innerHTML = new Intl.NumberFormat(getPreferredLang()).format(parcelasSP[Sesion.estado.ifn].size);
	// actualizo la URL
	const newurl = obtenerURL();
	if (window.location !== newurl)
		history.replaceState(Sesion.estado, "", newurl);
}


// PARCELAS
function parcelaFiltrada(ifn, piri) {
	//if (Sesion.taxones.length == 0) // caso sin taxones
	if (Sesion.estado.SP[ifn].tx.length == 0) // caso sin taxones
		return !pasaFiltroTaxon(piri, config.treeUri, ifn); // tiene que pasar el filtro de treeUri...
	// compruebo si hay que incluir la parcela analizando los taxones
	for (const tx of Sesion.estado.SP[ifn].tx) { // miro los taxones de la versión del IFN
		// recojo árboles por hectárea
		const nt = sumMeasureTaxon(Datos.parcelas[piri].nt, tx);
		if (nt == 0 && Sesion.taxonAnd)
			return true; // sin árboles => NO cumple el caso AND
		if (nt > 0) {
			// caso con árboles, comprobamos si pasa el filtro (de haberlo)
			let pasafiltro = pasaFiltroTaxon(piri, tx, ifn);
			if (pasafiltro && !Sesion.taxonAnd) 
				return false; // pasa el filtro => cumple el caso OR
			if (!pasafiltro && Sesion.taxonAnd)
				return true; // no pasa el filtro => NO cumple el caso AND
		}
	}
	// si llega aquí en el caso OR no cumple y en el caso AND sí cumple
	return !Sesion.taxonAnd;
}
function pasaFiltroTaxon(piri, tx, ifn) { // se usa para filtar y para los colores
	// si no hay ifn, cojo el de la sesión
	const txfilt = ifn === undefined? Sesion.taxonesFiltros : Sesion.estado.SP[ifn].txfilt;
	if (!txfilt[tx])
		return true;
	else {
		for (const par in txfilt[tx]) {
			const val = sumMeasureTaxon(Datos.parcelas[piri][par], tx);
			// compruebo si NO pasa el filtro
			if (val < txfilt[tx][par].min || 
					(val > txfilt[tx][par].max && config.maxValuesPlot[par] != txfilt[tx][par].max))
				return false;
		}
		return true;
	}

	/* TODO previo
	if (!Sesion.taxonesFiltros[tx])
		return true;
	else {
		for (const par in Sesion.taxonesFiltros[tx]) {
			const val = sumMeasureTaxon(Datos.parcelas[piri][par], tx);
			// compruebo si NO pasa el filtro
			if (val < Sesion.taxonesFiltros[tx][par].min || 
					(val > Sesion.taxonesFiltros[tx][par].max && config.maxValuesPlot[par] != Sesion.taxonesFiltros[tx][par].max))
				return false;
		}
		return true;
	}*/
}

/*
function parcelaFiltrada(ifn, piri) {
	if (Sesion.taxones.length == 0) // caso sin taxones
		return false;
	// compruebo si hay que incluir la parcela analizando los taxones
	for (const tx of Sesion.estado.SP[ifn].tx) { // miro los taxones de la versión del IFN
		// recojo árboles por hectárea
		const nt = sumMeasureTaxon(Datos.parcelas[piri].nt, tx);		
		if (nt > 0 && !Sesion.taxonAnd) 
			return false; // ya NO está filtrada (OR)
		if (nt == 0 && Sesion.taxonAnd)
			return true; // ya está filtrada (AND)
	}
	// si llega aquí en el caso OR está filtrada y en el caso AND no está filtrada
	return !Sesion.taxonAnd;
}*/

// colores parcelas
function colorParcela(plot) {
	// parcelas bloqueadas
	if (Sesion.estado.SP[Sesion.estado.ifn].bloq[plot.iri]) 
		return { colext: config.colblack, colint: config.colblack };
	// parcelas seleccionadas
	else if (parcelasSP[Sesion.estado.ifn].has(plot.iri)) 
		return { colext: config.colores[config.colregselind][8], 
			colint: config.colores[config.colregselind][5] };
	else { // resto de parcelas
		if (Sesion.taxones.length == 0) {
			// miro si hay filtro de treeUri
			if (!pasaFiltroTaxon(plot.iri, config.treeUri))
				return { colext: config.coloutext, colint: config.coloutint }; // no pasa el filtro
				//return { colext: config.colores[config.colplotind][8],
				//	colint: config.colores[config.colplotind][0] }; // no pasa el filtro
			else 
				return { colext: config.colores[config.colplotind][8],
					colint: config.colores[config.colplotind][getGradoColor(sumMeasureTaxon(plot.nt, config.treeUri))] };
		}
		else {
			// caso complicado, puede haber varios colores
			// preinicializo
			let colext = [];
			let colint = [];
			// extraigo los colores
			for (let ind = 0; ind < Sesion.taxones.length; ind++) {		
				const tx = Sesion.taxones[ind];
				// IMPORTANTE: compruebo si es el primero (si no lo es, no lo mostramos)
				if (Sesion.taxones.indexOf(tx) != ind)
					continue;
				const nt = sumMeasureTaxon(plot.nt, tx);
				if (nt > 0 && pasaFiltroTaxon(plot.iri, tx)) {
					// incluyo colores
					const indcoltx = Sesion.taxonesColores[ind];
					colext.push( config.colores[indcoltx][8] );
					colint.push( config.colores[indcoltx][getGradoColor(nt, tx)] );		
				}
			}
			// actúo según el resultado
			if (colext.length == 0)
				return { colext: config.coloutext, colint: config.coloutint }; // no pasa el filtro
				//return { colext: config.colores[config.colplotind][8],
				//	colint: config.colores[config.colplotind][0] }; // no pasa el filtro
			else if (colext.length == 1)
				return { colext: colext[0], colint: colint[0] }; // devuelvo un único color (sin arrays)
			else
				return { colext: getColorMix(colext), // en el exterior devuelvo mezcla
					colint: colint }; // en el interior devuelvo el vector de colores
		}			
	}
}
/* esto funciona bien sin filtros de taxones	
function colorParcela(plot, esBorde) {

	// inicializo color y grado
	let indcol = config.colplotind; // color parcela por defecto
	let indgrado = esBorde? 8 : 0;
	// parcelas bloqueadas
	if (Sesion.estado.SP[Sesion.estado.ifn].bloq[plot.iri]) 
		return config.colblack;
	// parcelas seleccionadas
	else if (parcelasSP[Sesion.estado.ifn].has(plot.iri)) {
		indcol = config.colregselind;
		if (!esBorde)
			indgrado = 5;
	}	
	else { // resto de parcelas
		if (Sesion.taxones.length == 0) {
			if (!esBorde)
				indgrado = getGradoColor(sumMeasureTaxon(plot.nt, config.treeUri));					
		}
		else {
			// caso complicado, puede haber varios colores
			const cols = [];
			for (let ind = 0; ind < Sesion.taxones.length; ind++) {			
				const tx = Sesion.taxones[ind];
				const nt = sumMeasureTaxon(plot.nt, tx);
				if (nt > 0) {
					const indcoltx = Sesion.taxonesColores[ind];
					const indgradotx = esBorde? 8 : getGradoColor(nt, tx);
					cols.push(config.colores[indcoltx][indgradotx]);
				}			
			}
			if (cols.length == 1)
				return cols[0]; // devuelvo el color
			else if (cols.length > 1) { 
				if (esBorde) // toca mezclar
					return getColorMix(cols);
				else
					return cols; // devuelvo un vector con los colores
			}
			// si no hubiera color en cols, va al caso general	
		}	
	}
	// retorno de un color sencillo
	return config.colores[indcol][indgrado];*/


	/* esto funciona bien si no hay taxones
	
	const indcol = Sesion.estado.SP[Sesion.estado.ifn].bloq[plot.iri]? config.coltesimpind : parcelasSP[Sesion.estado.ifn].has(plot.iri)? 
		config.colprovselind : config.colplotind;
	const indgrado = esBorde? 8 : Sesion.estado.SP[Sesion.estado.ifn].bloq[plot.iri]? 
		3 : getGradoColor(numArbsTaxon(plot.ntrees, config.treeUri));
	return config.colores[indcol][indgrado];
}*/

function getGradoColor(valor) {
	const valormax = config.ntSatParcela;
	const grados = config.colores[0].length;
	const delta = valormax / grados;
	// valor demasiado pequeño
	if (valor <= 0)
		return 0;
	// grados
	for (let ind=0; ind < grados; ind++) {
		// devuelvo color si está en el escalón adecuado
		if (valor >= ind*delta && valor < (ind+1)*delta)
			return ind;	
	}
	// valor demasiado grande
	return grados -1; 
}
// me baso en la librería mix-css-color
function getColorMix(colores) {
	//return colores[0];
	if (colores.length == 1)
		return colores[0];
	else if (colores.length == 2)
		return mix(colores[0], colores[1], 50).hex;
	else if (colores.length == 3) {
		const caux = mix(colores[0], colores[1], 50).hex;
		return mix(caux, colores[2], 67).hex;
	}
	else if (colores.length == 4) {
		const caux0 = mix(colores[0], colores[1], 50).hex;
		const caux1 = mix(colores[1], colores[2], 50).hex;
		return mix(caux0, caux1, 50).hex;
	}
	return undefined;
}

function ajustarColorParcelas(puris) {
	const repintar = []; // guardo parcelas a repintar (si cambió el número de segmentos)
	for (let puri of puris) {
		if (Sesion.parPintadas[puri]) {
			const plot = Datos.parcelas[puri];
			const { colext, colint } = colorParcela(plot);
			//const colint = colorParcela(plot, false);
			//const colext = colorParcela(plot, true);
				
			// número de colores
			const newnc = Array.isArray(colint)? colint.length : 1;
			const oldnc = Array.isArray(Sesion.parPintadas[puri])? Sesion.parPintadas[puri].length : 1;
			if (newnc != oldnc) { // toca repintar la parcela...
				// quito lo anterior
				if (oldnc == 1)
					Layers.parcs.removeLayer(Sesion.parPintadas[puri]);
				else {
					for (let ppint of Sesion.parPintadas[puri])
						Layers.parcs.removeLayer(ppint);
				}
				// borro y marco para repintar
				delete Sesion.parPintadas[puri];
				repintar.push(puri);
			}
			else { // sólo cambio el color
				if (newnc == 1) 
					Sesion.parPintadas[puri].setStyle( {fillColor: colint, color: colext} );
				else {
					for (let ind=0; ind<Sesion.parPintadas[puri].length; ind++)
						Sesion.parPintadas[puri][ind].setStyle( {fillColor: colint[ind], color: colext} );
				}
			}	
		}
	}
	// llamo a repintar
	if (repintar.length > 0) 
		pintarParcelas(repintar);
}


// popups parcelas
function popupParcela(plot) {
	// devuelvo un objeto con el popup y las opciones de rendering
	const out = {
		popup: {},
		opcs: {} // caso sin canvas
	};
	// preparo objeto para la plantilla del mustache
	const parctemp = {};
	// iri
	parctemp.iri = plot.iri;
	// nombre parcela
	//parctemp.plot = getLiteral(dict.plot) + " " + applyPrefix(plot.iri, config.prefixes);
	parctemp.plot = getLiteral(dict.plot) + " " + uriToLiteral(plot.iri, "plot-");
	// fuente
	parctemp.source = getLiteral(dict["source"+plot.ifn]);
	// dataCapture
	if (plot.dataCapture) {
		const dclab = getDate(plot.dataCapture);
		if (dclab)	
			parctemp.dataCapture = getLiteral(dict.dataCapture) + " " + dclab;
	}
	// info stock
	// me baso en los árboles/Ha para ver si hay datos de árboles
	const nt = sumMeasureTaxon(plot.nt, config.treeUri);
	if (nt == 0) {
		parctemp.notrees = true;
		parctemp.stockinfo = getLiteral(dict.notreedata);
	}
	else {	// pongo canvas para el chart
		parctemp.chart = true;
		out.opcs = {minWidth: 360};
		// formateo stockinfo
		parctemp.stockinfo = nt.toLocaleString(getPreferredLang()) + " " + getLiteral(dict.treesperha);
		// incluyo el resto de la información de stock
		const ba = sumMeasureTaxon(plot.ba, config.treeUri);
		if (ba) 
			parctemp.stockinfo += '<br>' + getLiteral(dict.basalareamin) + ba.toLocaleString(getPreferredLang()) + ' m<sup>2</sup>/ha';
		const vwb = sumMeasureTaxon(plot.vwb, config.treeUri);
		if (vwb) 
			parctemp.stockinfo += '<br>' + getLiteral(dict.volumewithbarkmin) + vwb.toLocaleString(getPreferredLang()) + ' m<sup>3</sup>/ha';
		const vwob = sumMeasureTaxon(plot.vwob, config.treeUri);
		if (vwob) 
			parctemp.stockinfo += '<br>' + getLiteral(dict.volumewithoutbarkmin) + vwob.toLocaleString(getPreferredLang()) + ' m<sup>3</sup>/ha';			
		// alternativa con colores en los valores
		/* no la pongo porque no es inmediato si no se desglosa por especie seleccionada
		// formateo stockinfo
		parctemp.stockinfo = '';
		const objs = [];
		objs.push( { prop : "nt", labini : "", un: " "+getLiteral(dict.treesperha) } );
		objs.push( { prop : "ba", labini : getLiteral(dict.basalareamin), un: " m2/ha" } );
		objs.push( { prop : "vwb", labini : getLiteral(dict.volumewithbarkmin), un: " m3/ha" } );
		objs.push( { prop : "vwob", labini : getLiteral(dict.volumewithoutbarkmin), un: " m3/ha" } );
		// itero
		for (let obj of objs) {
			if (plot[obj.prop]) {
				const val = sumMeasureTaxon(plot[obj.prop], config.treeUri);
				// todo esto es para ver si pongo el valor en rojo por no pasar el filtro
				let rojo = false;
				if (Sesion.taxones.length == 0)
					rojo = !pasaFiltroPar(plot.iri, obj.prop, config.treeUri);
				else {
					for (const tx of Sesion.taxones) {
						if (!pasaFiltroPar(plot.iri, obj.prop, tx)) {
							rojo = true;
							break;						
						}				
					}
				}
				// formateo
				parctemp.stockinfo += obj.labini;
				if (rojo)
					parctemp.stockinfo += '<span class="text-danger">';
				parctemp.stockinfo += val.toLocaleString(getPreferredLang());
				if (rojo)
					parctemp.stockinfo += '</span>';
				parctemp.stockinfo += obj.un + '<br>';
			}
		}*/
	}
	// extraigo status y ajusto botones
	if (Sesion.estado.SP[Sesion.estado.ifn].manual[plot.iri]) {
		parctemp.status = getLiteral(dict.manualPlot); // manual
		parctemp.statusClass = "text-warning";
		parctemp.bloq = true; // habilito botón de bloquear
	}	
	else if (Sesion.estado.SP[Sesion.estado.ifn].bloq[plot.iri]) {
		parctemp.status = getLiteral(dict.blockedPlot); // blocked
		parctemp.statusClass = "text-black";
		parctemp.unbloq = true; // habilito botón de desbloquear
	}
	else if (parcelasSP[Sesion.estado.ifn].has(plot.iri)) {
		parctemp.status = getLiteral(dict.selectedPlot); // seleccionada
		parctemp.statusClass = "text-warning";
		parctemp.bloq = true; // habilito botón de bloquear
	}	
	else {
		parctemp.status = getLiteral(dict.unselectedPlot); // no seleccionada
		parctemp.statusClass = "text-secondary";
		parctemp.manual = true; // habilito botón de selección manual
	}
	
	// obtengo el popup y lo devuelvo
	out.popup = Mustache.render(plotPopupTemplate, parctemp);
	
	return out;
}

function ponerChartPopupParcela(canvas, piri) {
	// muestro el canvas
	canvas.classList.remove("d-none");
	
	// preparo los datos del chart
	const measures = [
		{ 
			id: "nt",
			label: getLiteral(dict.treedensity),
			unit: getLiteral(dict.treesperha),
			ndecs: 2
		},	
		{ 
			id: "ba",
			label: getLiteral(dict.basalarea),
			unit: "m2/ha",
			ndecs: 2
		},
		{ 
			id: "vwb",
			label: getLiteral(dict.volumewithbark),
			unit: "m3/ha",
			ndecs: 2
		},
		{ 
			id: "vwob",
			label: getLiteral(dict.volumewithoutbark),
			unit: "m3/ha",
			ndecs: 2
		}
	];
	const species = {}; // uno por cada tipo de medida
	const datasets = {}; // uno por cada tipo de medida
	for (const measure of measures) {
		const items = [];
		for (const tx in Datos.parcelas[piri][measure.id]) {		
			// etiqueta de especie
			const labtx = getTaxonLabel(tx, Sesion.nomci);
			//if (!species.includes(labtx))
			//	species.push(labtx);				
			// preparo el item y guardo
			const item = {
				tx: tx,
				label: labtx,
				valor: Number(Datos.parcelas[piri][measure.id][tx]).toFixed(measure.ndecs),//Datos.parcelas[piri][measure.id][tx]
				indcols: [ config.colplotind ] // preinicializo al color de la parcela
			};
			items.push(item);	
			// si hay taxones seleccionados, se complica la selección del color
			if (Sesion.taxones.length > 0) {
				item.indcols = [];
				const txel = { [tx]: Datos.parcelas[piri][measure.id][tx] }; // sólo evalúo el taxón actual
				// incluyo tantos índices de color como corresponda
				for (let ind = 0; ind < Sesion.taxones.length; ind++) {			
					const txev = Sesion.taxones[ind];
					const nttx = sumMeasureTaxon(txel, txev);
					if (nttx > 0) // incluyo si hay algo
						item.indcols.push(Sesion.taxonesColores[ind]);
				}
				// cubro el caso de que tx no coincida con ningún taxón seleccionado
				if (item.indcols.length == 0)
					item.indcols = [ config.colplotind ];
			}
		}
		// ordeno los items por número de árboles
		items.sort((a, b) => b.valor - a.valor );
		// preparo los índices de los colores (una vez ordenados los items)
		const indcolstx = {};
		for (const item of items) {
			// incluyo para cada índice de colores este taxón (para el reparto de colores)
			for (const ic of item.indcols) {
				if (!indcolstx[ic])
					indcolstx[ic] = [];
				indcolstx[ic].push(item.tx);		
			}	
		}
		// asigno los colores
		for (const item of items) {
			const colsitem = [];
			for (const ic of item.indcols) {
				// obtengo el grado según el número de elementos en indcolstx y la posición del ítem
				const ne = indcolstx[ic].length; // total de taxones con ese mismo color
				const pos = indcolstx[ic].indexOf(item.tx); // la posición del taxón en este grupo
				const indgrado = config.colchartinds[ne][pos]; // el grado de intensidad según colchartinds
				//console.log ("ne: " + ne + " - pos: " + pos + " - ig: " + indgrado);
				// obtengo y guardo el color
				colsitem.push(config.colores[ic][indgrado]);		
			}
			// y guardo el color definitivo haciendo un colormix si hiciera falta
			item.color = getColorMix(colsitem);
		}
		// completo el dataset y lo guardo		
		species[measure.id] = items.map((item) => item.label);
		datasets[measure.id] = {
			label: measure.unit,
			data: items.map((item) => item.valor),
			backgroundColor: items.map((item) => item.color),
			hoverOffset: 4
		};
	}
	// preparo el chart
	const chartconfig =  {
		type: 'doughnut',
		data: {
			labels: species.nt,
			datasets: [ datasets.nt ]
		},
		options: {
			animation: false
		}
	};	
	// pinto
	const doughchart = new Chart(canvas, chartconfig);
		
	// pongo listener al dropdown
	document.querySelectorAll('.isstock').forEach(el => {
		el.addEventListener('click', function (ev) {
			ev.preventDefault();
			// cojo el índice
			const ind = Number(this.getAttribute('ind'));
			// actualizo la base de datos con la medida elegida
			doughchart.data.labels = species[measures[ind].id];
			doughchart.data.datasets = [ datasets[measures[ind].id] ];
			// cambio etiqueta del botón
			document.getElementById('botonInfoSpeciesStock').innerHTML = ev.target.innerHTML;
			// Refresh the chart
			doughchart.update();
		});
	});
}

// cambio estrategia de reajuste de popups: como regenerarlos todos puede ser lento,
// simplemente marco las parcelas con un regenerado de popup pendiente
// como capturo el evento de popupopen en map.js, hago ahí el regenerado
function ajustarPopupsParcelas(puris) {
	for (let puri of puris) {
		if (Sesion.parPintadas[puri]) 
			Sesion.parPopupsRegenerar[puri] = true; // sólo marco las parcelas que requieren regenerar el popup
	}
	// si hay un popup abierto miro si hay que actualizarlo
	if (Sesion.popup && Sesion.popup.isOpen()) {
		// cojo la IRI del popup
		// cojo el DOM element del popup
		const popel = Sesion.popup.getElement();
		// cojo el DOM element de la clase popup
		const popupel = popel.querySelector(".popup");
		// si es de tipo parcela entonces miro si hay que repintar el popup
		if (popupel && popupel.classList.contains("parcela")) {
			// extraigo la iri
			const piri = popel.querySelector(".popup").getAttribute("iri");
			// si corresponde a una parcela pintada y está contenida en puris, entonces repinto el popup
			if (Sesion.parPintadas[piri] && puris.includes(piri)) {// extraigo coords del popup
				const latlng = Sesion.popup.getLatLng();
				Mimapa.closePopup();
				if (Array.isArray(Sesion.parPintadas[piri]))
					Sesion.parPintadas[piri][0].openPopup(latlng);
				else 
					Sesion.parPintadas[piri].openPopup(latlng);
			}
		}
	}
}

function pintarParcelas(puris) {
	// pinto las parcelas pendientes
	let plots = [];
	for (let puri of puris) {
		// sólo pintamos si no estaba la parcela en la capa
		if (!Sesion.parPintadas[puri]) {
			const plot = Datos.parcelas[puri];
			const pcentro = L.latLng(plot.lat, plot.lng);
			const { colext, colint } = colorParcela(plot);
			//const colint = colorParcela(plot, false);
			//const colext = colorParcela(plot, true);			
			const op = popupParcela(plot); // obtengo el popup
			// si hay varios colores...
			if (Array.isArray(colint)) {
				// creo un sector circular por color
				const grados = 360/colint.length;
				Sesion.parPintadas[plot.iri] = [];
				for (let ind=0; ind<colint.length; ind++) {
					Sesion.parPintadas[plot.iri].push(
						L.circle(pcentro, {color: colext, weight: 2, fillColor: colint[ind],
								fillOpacity: 0.75, radius: Sesion.radioParPintadas, startAngle: grados*ind, endAngle: grados*(ind+1)})
							.bindPopup(op.popup, op.opcs)
							.on('dblclick ', function(e) {
								cambiarSeleccionParcelaSP(puri, false);
							}) // añado aquí también handler de dblclick para inclusión manual o bloqueo de parcela	
							.addTo(Layers.parcs)
					);
				}
			}
			else { // sólo un color
				// creo círculo y lo guardo, lo añado al layer group, pongo popup y pongo handler de doble click para cambiar selección
				Sesion.parPintadas[plot.iri] = L.circle(pcentro, {color: colext, weight: 2, fillColor: colint,
						fillOpacity: 0.75, radius: Sesion.radioParPintadas})				
					.bindPopup(op.popup, op.opcs)
					.on('dblclick ', function(e) {
						cambiarSeleccionParcelaSP(puri, false);
					}) // añado aquí también handler de dblclick para inclusión manual o bloqueo de parcela	
					.addTo(Layers.parcs);
			}
			// no hay popup pendiente de regenerar
			delete Sesion.parPopupsRegenerar[puri];
		}
	}
	// si quedó de abrir un popup de parcela...
	if (popupParPendiente && puris.includes(popupParPendiente)) {		
		// al ser parcelas seleccionadas o bloqueadas nunca habrá un array, pero por si acaso
		if (Array.isArray(Sesion.parPintadas[popupParPendiente]))
			Sesion.parPintadas[popupParPendiente][0].openPopup();
		else 
			Sesion.parPintadas[popupParPendiente].openPopup();
		popupParPendiente = null;
	}
}

async function quitarParcelas() {
	console.info("Borrando capa de parcelas...");
	// borro la capa de parcelas
	Layers.parcs.clearLayers();
	// inicializo la lista de parcelas pintadas
	Sesion.parPintadas = {};	
}


export { parcelasSP, inicializarSP, cargarSP, cambiarSeleccionRegionSP, incluirPoligonoSP, borrarPoligonoSP,
	cambiarSeleccionParcelaSP, pintarParcelas, ajustarColorParcelas, ajustarPopupsParcelas, popupParcela, 
	ponerChartPopupParcela, ponerChartPopupPoli, quitarParcelas };